import { Dialog, Transition } from "@headlessui/react";
import { StarIcon as StarOutlineIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import { Fragment, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { useAnalytics } from "../lib/analytics";
import { api } from "../lib/api";
import { useClientTranslations } from "../lib/i18n/client";
import styles from "./FeedbackModal.module.css";

interface FeedbackModalProps {
	isOpen: boolean;
	onClose: () => void;
	messageId: string | null;
	chatId: string | null;
}

export function FeedbackModal({
	isOpen,
	onClose,
	messageId,
	chatId,
}: FeedbackModalProps) {
	const { t, isRTL, direction } = useClientTranslations("feedback");
	const analytics = useAnalytics();
	const feedbackId = useId();
	const [rating, setRating] = useState(0);
	const [feedback, setFeedback] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const panelRef = useRef<HTMLDivElement>(null);
	const dragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
	const isDragging = useRef(false);
	const dragHandleRef = useRef<HTMLButtonElement>(null);
	let animationFrameId: number | null = null;

	// Draggable logic
	const handleMouseDown = (e: React.MouseEvent) => {
		if (panelRef.current && dragHandleRef.current?.contains(e.target as Node)) {
			isDragging.current = true;
			const rect = panelRef.current.getBoundingClientRect();
			dragOffset.current = {
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			};
			document.body.classList.add("select-none");
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
		}
	};

	const handleMouseMove = (e: MouseEvent) => {
		if (isDragging.current && panelRef.current) {
			if (animationFrameId) cancelAnimationFrame(animationFrameId);
			animationFrameId = requestAnimationFrame(() => {
				if (panelRef.current) {
					panelRef.current.style.left = `${e.clientX - dragOffset.current.x}px`;
					panelRef.current.style.top = `${e.clientY - dragOffset.current.y}px`;
					panelRef.current.style.position = "fixed";
					panelRef.current.style.margin = "0";
				}
			});
		}
	};

	const handleMouseUp = () => {
		isDragging.current = false;
		document.body.classList.remove("select-none");
		document.removeEventListener("mousemove", handleMouseMove);
		document.removeEventListener("mouseup", handleMouseUp);
		if (animationFrameId) cancelAnimationFrame(animationFrameId);
	};

	const handleRating = (value: number) => {
		setRating(value);
		analytics.logEvent("feedback_rating_selected", value);
	};

	const handleCancel = () => {
		analytics.logEvent("feedback_cancelled");
		analytics.logEvent("feedback_had_rating", rating > 0 ? "yes" : "no");
		analytics.logEvent("feedback_had_text", feedback.length > 0 ? "yes" : "no");
		onClose();
	};

	const handleSubmit = async () => {
		setIsSubmitting(true);
		try {
			await api.post("/api/feedback", {
				rating,
				feedback,
				messageId,
				chatId,
			});

			analytics.logEvent("feedback_submitted");
			analytics.logEvent("feedback_rating", rating);
			analytics.logEvent("feedback_text_length", feedback.length);

			toast.success(t("toast.success"), {
				duration: 3000,
			});

			onClose();
		} catch (error) {
			console.error("Error submitting feedback:", error);
			toast.error(t("toast.error"));
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-50" onClose={handleCancel}>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className={styles.overlay} />
				</Transition.Child>

				<div className={styles.container}>
					<div className={styles.centered}>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<Dialog.Panel
								ref={panelRef}
								className={styles.panel}
								style={{ position: "relative", direction }}
							>
								<button
									type="button"
									ref={dragHandleRef}
									onMouseDown={handleMouseDown}
									className={styles.dragHandle}
									style={{ userSelect: "none" }}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
										}
									}}
								>
									<Dialog.Title as="h3" className={styles.title}>
										{t("title")}
									</Dialog.Title>
									<div className={styles.starsContainer}>
										<div
											className={`${styles.starsRow} ${isRTL ? styles.rtl : ""}`}
										>
											{[1, 2, 3, 4, 5].map((star) => (
												<button
													type="button"
													key={star}
													onClick={() => handleRating(star)}
													className={styles.starButton}
												>
													{star <= rating ? (
														<StarIcon
															className={`${styles.starIcon} ${styles.starIconFilled}`}
														/>
													) : (
														<StarOutlineIcon
															className={`${styles.starIcon} ${styles.starIconEmpty}`}
														/>
													)}
												</button>
											))}
										</div>
									</div>
								</button>

								<div className={styles.textareaContainer}>
									<label
										htmlFor={feedbackId}
										className={`${styles.label} ${isRTL ? styles.rtl : ""}`}
									>
										{t("additionalFeedback.label")}
										<div
											className={`${styles.helpText} ${isRTL ? styles.rtl : ""}`}
										>
											<p>{t("additionalFeedback.help1")}</p>
											<p>{t("additionalFeedback.help2")}</p>
										</div>
									</label>
									<textarea
										id={feedbackId}
										rows={4}
										value={feedback}
										onChange={(e) => setFeedback(e.target.value)}
										className={`${styles.textarea} ${isRTL ? styles.rtl : ""}`}
										placeholder={t("placeholder")}
									/>
								</div>

								<div className={`${styles.buttons} ${isRTL ? styles.rtl : ""}`}>
									<button
										type="button"
										className={styles.cancelButton}
										onClick={handleCancel}
									>
										{t("buttons.cancel")}
									</button>
									<button
										type="button"
										className={styles.submitButton}
										onClick={handleSubmit}
										disabled={isSubmitting}
									>
										{isSubmitting
											? t("buttons.submitting")
											: t("buttons.submit")}
									</button>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
